import GameLifeline from "../GameBoard/GameLifeline";
import { useState, useEffect, useRef, useMemo } from "react";
import ReportQuestionModal from "../../components/Modal/ReportQuestionModal";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import GameAnswerTimerClock from "./Answer/GameAnswerTimerClock";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { getGameDetailsAPI, useLifelineAPI, getQrCodeTimerSettingsAPI } from "../../api/game";
import useToast from "../../hooks/useToast";
import Loader from "../../components/Loader/Loader";
import { useTranslation } from "react-i18next";
import PrimaryBtn from "../../components/button/PrimaryBtn";
import GameAnswerQuestionContent from "./Answer/GameAnswerQuestionContent";
import GameAnswerCorrectAnswerContent from "./Answer/GameAnswerCorrectAnswerContent";
import { useGameDetailsFromLayout } from "../../layouts/Game/GameDetailsContext";

import { getDefaultPortraitByGender, resolveCharacterUrlFromList } from "../../utils/charcter";
import { getCharacterListAPI } from "../../api/user";

function getLocalizedField(value: unknown, lang: string): string {
  if (value == null) return "";
  if (typeof value === "string") return value;
  if (typeof value === "object" && !Array.isArray(value)) {
    const o = value as Record<string, string>;
    return String(o[lang] ?? o.en ?? o.ar ?? "");
  }
  return String(value);
}



const Game1000Question = () => {
  const { t } = useTranslation();
  const [showCorrectAnswer, setShowCorrectAnswer] = useState(false);
  const [showAnswerModal, setShowAnswerModal] = useState(false);
  const [selectedAnswer, setSelectedAnswer] = useState("");
  const [selectedParticipantIds, setSelectedParticipantIds] = useState<string[]>([]);
  const [selectedTeamId, setSelectedTeamId] = useState<string | null>(null);
  const [showReportModal, setShowReportModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const SECOND_CHANCE_SECONDS = 20;
  const [isSecondChanceActive, setIsSecondChanceActive] = useState(false);
  const [isSecondChanceExpired, setIsSecondChanceExpired] = useState(false);
  const [playerTimerPaused, setPlayerTimerPaused] = useState(false);
  const [secondChanceKey, setSecondChanceKey] = useState(0);
  const elapsedSecondsRef = useRef(0);
  const secondChanceSecondsRef = useRef(SECOND_CHANCE_SECONDS);
  const [optionImagePreviewUrl, setOptionImagePreviewUrl] = useState<string | null>(null);

  const { gameId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const currentLanguage = localStorage.getItem("i18nextLng") || "en";
  const gameDetailsFromLayout = useGameDetailsFromLayout();
  const setIsLifelineUsedOnQuestion = gameDetailsFromLayout?.setIsLifelineUsedOnQuestion;

  useEffect(() => {
    setIsLifelineUsedOnQuestion?.(true);
  }, [setIsLifelineUsedOnQuestion]);

  // Get the 1000-point question from navigation state
  const questionFromState = location.state?.question;
  const teamIdFromState = location.state?.teamId;
  const teamNameFromState = location.state?.teamName;
  const lifelineIdFromState = location.state?.lifelineId;
  const teamLifelineIdFromState = location.state?.teamLifelineId;
  // Redirect if no question data
  useEffect(() => {
    if (!questionFromState) {
      navigate(`/game/${gameId}`);
    }
  }, [questionFromState, gameId, navigate]);

  // Fetch game data from API for team info
  const { data: gameDetails, isLoading: isLoadingGame } = useQuery({
    queryKey: ["gameDetails", gameId],
    queryFn: () => getGameDetailsAPI(gameId as string),
    enabled: !!gameId,
  });

  const gameData = gameDetails?.data?.game;
  const teamData = gameData?.teams || [];
  // The question data from the API response
  const questionData = questionFromState;

  const { data: qrTimerSettingsData } = useQuery({
    queryKey: ["qrCodeTimerSettings"],
    queryFn: getQrCodeTimerSettingsAPI,
    staleTime: 5 * 60 * 1000,
  });

  const qrTimerCap = useMemo(() => {
    const difficulty = questionData?.difficulty as "easy" | "medium" | "hard" | undefined;
    const settings = qrTimerSettingsData?.data;
    if (difficulty && settings?.[difficulty]) {
      return settings[difficulty];
    }
    return 60;
  }, [questionData?.difficulty, qrTimerSettingsData?.data]);

  const { data: portraitCharacterListData } = useQuery({
    queryKey: ["characterList", "portrait"],
    queryFn: () => getCharacterListAPI({ type: "portrait" }),
    staleTime: 1000 * 60 * 30,
  });
  const portraitCharacters = (portraitCharacterListData as any)?.data?.characters || [];
  // Submit answer mutation
  const { mutate: submitAnswer } = useMutation({
    mutationFn: useLifelineAPI,
    onSuccess: async (data) => {
      useToast(data?.message || t("game1000Question.answerSubmittedSuccessfully"), "success");

      // Await invalidation so fresh data (updated points) is in cache before navigating
      await queryClient.invalidateQueries({ queryKey: ["gameDetails", gameId] });
      // Navigate back to game board
      navigate(`/game/${gameId}`);
    },
    onError: (error: any) => {
      useToast(error.message || t("game1000Question.failedToSubmitAnswer"), "error");
      setIsSubmitting(false);
    },
  });

  const [participants, setParticipants] = useState<any[]>([]);

  // Handle answer click - when a team is selected
  const handleAnswerClick = (answer: string, teamId: string) => {
    const team = gameData?.teams?.find((t: any) => t._id === teamId);
    const teamParticipants = team?.participants || [];
    setParticipants(teamParticipants);
    setSelectedAnswer(answer);
    setSelectedTeamId(teamId);

    // Auto-select if only one participant in the team
    if (teamParticipants.length === 1) {
      setSelectedParticipantIds([teamParticipants[0]?.user_id?._id]);
    } else {
      setSelectedParticipantIds([]);
    }

    setShowAnswerModal(true);
  };

  const handleNoneAnswered = () => {
    setSelectedParticipantIds([]);
    setSelectedTeamId(null);
    setShowAnswerModal(false);
    submitAnswer({
      game_id: gameId,
      question_id: questionData?.used_question_id,
      user_id: [],
      lifeline_id: lifelineIdFromState,
      team_lifeline_id: teamLifelineIdFromState,
      team_id: teamIdFromState,
      answered_team_id: null,
    });
  };

  // Handle participant selection - toggle multi-select
  const handleParticipantClick = (participantId: string) => {
    setSelectedParticipantIds((prev) =>
      prev.includes(participantId) ? prev.filter((id) => id !== participantId) : [...prev, participantId]
    );
  };

  // Confirm the answer selection and call API
  const handleConfirmAnswer = () => {
    if (!questionData?.used_question_id || !selectedTeamId || !gameId) return;

    setIsSubmitting(true);

    // Call API to submit answer with lifeline team + answering team (participants optional)
    submitAnswer({
      game_id: gameId,
      question_id: questionData?.used_question_id,
      user_id: selectedParticipantIds,
      lifeline_id: lifelineIdFromState,
      team_lifeline_id: teamLifelineIdFromState,
      team_id: teamIdFromState,
      answered_team_id: selectedTeamId,
    });

    // Close modal and reset states
    setShowAnswerModal(false);
    setSelectedParticipantIds([]);
    setSelectedTeamId(null);
  };

  const handleStartSecondChance = () => {
    setIsSecondChanceExpired(false);
    setPlayerTimerPaused(false);
    setIsSecondChanceActive(true);
    secondChanceSecondsRef.current = SECOND_CHANCE_SECONDS;
    setSecondChanceKey((k) => k + 1);
  };

  const handleSecondChanceTimeUp = () => {
    secondChanceSecondsRef.current = 0;
    setIsSecondChanceExpired(true);
  };

  const handleResumePlayerTimer = () => {
    setIsSecondChanceExpired(false);
    setIsSecondChanceActive(false);
    setPlayerTimerPaused(true);
  };

  const getPreviewTimerSeconds = () => {
    if (isSecondChanceActive) {
      return isSecondChanceExpired ? 0 : secondChanceSecondsRef.current;
    }
    return elapsedSecondsRef.current;
  };

  const isMainTimerStopped = useMemo(
    () => showCorrectAnswer || showReportModal || showAnswerModal,
    [showCorrectAnswer, showReportModal, showAnswerModal],
  );

  // Show loading state
  if (isLoadingGame || isSubmitting || !questionData) {
    return (
      <div className="flex justify-center bg-primary-ivory items-center h-full min-h-[50vh]">
        <Loader />
      </div>
    );
  }

  return (
    <>
      <div className="wrapper flex-1 flex flex-col sm:flex-row bg-no-repeat xl:bg-[length:100%_100%] bg-cover bg-bottom bg-primary-ivory px-6 gap-4">
        <section className="w-full md:w-[80%] flex justify-center items-center relative flex-1 min-h-0">
          <div className="w-full h-full flex justify-center items-center 2xl:pb-[1.9vw] md:pb-6 pb-5 landscape:max-lg:pb-5">
            <div className="w-full mx-auto relative z-20 h-full 2xl:pt-[1vw] xl:pt-3 pt-2 flex flex-col min-h-0">
              {/* <button
                type="button"
                onClick={() => setShowReportModal(true)}
                className="absolute top-3 start-5 z-10 cursor-pointer text-light-vermilion font-garamond font-bold 2xl:text-[0.9375vw]/[1.04vw] xl:text-base/tight text-sm/tight inline-flex justify-center items-center 2xl:gap-[0.5vw] md:gap-2 gap-1 2xl:px-[0.83vw] xl:px-4 md:px-3 px-2.5"
              >
                <span className="2xl:w-[1.2vw] xl:w-5 w-4 2xl:h-[1.2vw] xl:h-5 h-4">
                  <svg className="w-full h-full" width="26" height="24" viewBox="0 0 26 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <g filter="url(#filter0_d_1000q_report)">
                      <path
                        d="M14.8728 22.75C17.5036 22.75 19.5804 22.75 21.1186 22.521C22.6805 22.2883 23.9475 21.7836 24.647 20.5488L24.7679 20.3165C25.3177 19.1463 25.0927 17.875 24.5308 16.5019C24.0833 15.4081 23.365 14.1147 22.4573 12.5644L21.4889 10.9272L19.2433 7.14127L19.1897 7.05013C17.9032 4.8811 16.8852 3.16549 15.9381 2.00065C14.9717 0.812175 13.9236 0 12.5418 0C11.1599 4.66667e-05 10.1118 0.812175 9.14544 2.00065C8.42528 2.88643 7.6636 4.09045 6.77451 5.57014L5.84026 7.14127L3.59466 10.9272L3.53884 11.0207C2.19875 13.2799 1.14133 15.0633 0.552674 16.5019C-0.0465261 17.9664 -0.262313 19.3151 0.436462 20.5488L0.574315 20.7722C1.29565 21.8457 2.50076 22.3029 3.96494 22.521C5.50313 22.75 7.57992 22.75 10.2107 22.75H14.8728ZM12.5418 14.2917C11.8975 14.2915 11.3751 13.7692 11.3751 13.125V7.875C11.3751 7.23072 11.8975 6.70841 12.5418 6.70833C13.186 6.70833 13.7085 7.23066 13.7085 7.875V13.125C13.7085 13.7693 13.186 14.2917 12.5418 14.2917ZM12.5418 18.3773C11.8975 18.3772 11.3751 17.8549 11.3751 17.2107V17.1992C11.3751 16.5549 11.8975 16.0327 12.5418 16.0326C13.186 16.0326 13.7085 16.5549 13.7085 17.1992V17.2107C13.7085 17.8549 13.186 18.3773 12.5418 18.3773Z"
                        fill="#FF6C6C"
                      />
                    </g>
                    <defs>
                      <filter
                        id="filter0_d_1000q_report"
                        x="0"
                        y="0"
                        width="25.884"
                        height="23.95"
                        filterUnits="userSpaceOnUse"
                        colorInterpolationFilters="sRGB"
                      >
                        <feFlood floodOpacity="0" result="BackgroundImageFix" />
                        <feColorMatrix
                          in="SourceAlpha"
                          type="matrix"
                          values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
                          result="hardAlpha"
                        />
                        <feOffset dx="0.8" dy="1.2" />
                        <feComposite in2="hardAlpha" operator="out" />
                        <feColorMatrix
                          type="matrix"
                          values="0 0 0 0 0.537255 0 0 0 0 0.0235294 0 0 0 0 0.0235294 0 0 0 1 0"
                        />
                        <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_1000q_report" />
                        <feBlend
                          mode="normal"
                          in="SourceGraphic"
                          in2="effect1_dropShadow_1000q_report"
                          result="shape"
                        />
                      </filter>
                    </defs>
                  </svg>
                </span>
                <span className="underline">{t("game1000Question.report")}</span>
              </button> */}

              <div className="hidden lg:flex justify-center gap-2 shrink-0 flex-wrap absolute -top-1 start-10">
                <div className="skew-x-[-10deg] 2xl:border-[0.104vw] border-[1.5px] border-black-300 bg-gradient-to-r from-yellow-400 via-yellow-500 to-orange-500 flex items-center 2xl:py-[0.5vw] xl:py-1.5 py-1 2xl:px-[1.042vw] lg:px-4 md:px-3 px-2.5 2xl:rounded-[0.26vw] rounded-[5px] 2xl:shadow-[0px_0.156vw_0px_#110706] shadow-[0px_2px_0px_#110706]">
                  <span className="skew-x-[10deg] 2xl:mt-[0.208vw] 2xl:text-[0.9375vw]/[1.04vw] xl:text-base/tight text-sm/tight font-bold text-white font-garamond">
                    {t("game1000Question.1000PointQuestion")}
                  </span>
                </div>
                {teamNameFromState && (
                  <div className="skew-x-[-10deg] 2xl:border-[0.104vw] border-[1.5px] border-black-300 bg-[#E7E6E6] flex items-center 2xl:py-[0.5vw] xl:py-1.5 py-1 2xl:px-[1.042vw] lg:px-4 md:px-3 px-2.5 2xl:rounded-[0.26vw] rounded-[5px] 2xl:shadow-[0px_0.156vw_0px_#110706] shadow-[0px_2px_0px_#110706]">
                    <span className="skew-x-[10deg] 2xl:mt-[0.208vw] 2xl:text-[0.9375vw]/[1.04vw] xl:text-base/tight text-sm/tight font-bold text-black-300">{teamNameFromState}</span>
                  </div>
                )}
              </div>

              <div className="hidden lg:block absolute top-3 start-1/2 -translate-x-1/2 -translate-y-1/2 rtl:translate-x-1/2 z-10">
                <GameAnswerTimerClock
                  isSecondChanceActive={isSecondChanceActive}
                  isSecondChanceExpired={isSecondChanceExpired}
                  playerTimerPaused={playerTimerPaused}
                  secondChanceKey={secondChanceKey}
                  questionType={questionData?.question_type}
                  qrTimerCap={qrTimerCap}
                  isMainTimerStopped={isMainTimerStopped}
                  onStartSecondChance={handleStartSecondChance}
                  onSecondChanceTimeUp={handleSecondChanceTimeUp}
                  onResumePlayerTimer={handleResumePlayerTimer}
                  secondChanceSeconds={SECOND_CHANCE_SECONDS}
                  elapsedSecondsRef={elapsedSecondsRef}
                  secondChanceSecondsRef={secondChanceSecondsRef}
                />
              </div>

              <button
                onClick={() => setShowReportModal(true)}
                className="col-span-2 cursor-pointer text-light-vermilion  justify-center items-center 2xl:gap-[0.5vw] md:gap-2 gap-1 2xl:px-[0.83vw] xl:px-4 md:px-3 px-2.5 hidden sm:flex absolute -bottom-2 start-10  z-10 bg-[#FFFF] rounded-md p-1 border border-black-300 h-8 font-bold 2xl:text-[1.89vw] xl:text-xl/tight lg:text-base/tight text-sm/tight landscape:max-lg:text-sm/tight 2xl:py-[1.2vw] xl:py-3 lg:py-3 md:py-2 py-1"
              >
                <span className="2xl:w-[1.2vw] xl:w-5 w-4 2xl:h-[1.2vw] xl:h-5 h-4">
                  <svg
                    className="w-full h-full"
                    width="26"
                    height="24"
                    viewBox="0 0 26 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <g filter="url(#filter0_d_3457_147464)">
                      <path
                        d="M14.8728 22.75C17.5036 22.75 19.5804 22.75 21.1186 22.521C22.6805 22.2883 23.9475 21.7836 24.647 20.5488L24.7679 20.3165C25.3177 19.1463 25.0927 17.875 24.5308 16.5019C24.0833 15.4081 23.365 14.1147 22.4573 12.5644L21.4889 10.9272L19.2433 7.14127L19.1897 7.05013C17.9032 4.8811 16.8852 3.16549 15.9381 2.00065C14.9717 0.812175 13.9236 0 12.5418 0C11.1599 4.66667e-05 10.1118 0.812175 9.14544 2.00065C8.42528 2.88643 7.6636 4.09045 6.77451 5.57014L5.84026 7.14127L3.59466 10.9272L3.53884 11.0207C2.19875 13.2799 1.14133 15.0633 0.552674 16.5019C-0.0465261 17.9664 -0.262313 19.3151 0.436462 20.5488L0.574315 20.7722C1.29565 21.8457 2.50076 22.3029 3.96494 22.521C5.50313 22.75 7.57992 22.75 10.2107 22.75H14.8728ZM12.5418 14.2917C11.8975 14.2915 11.3751 13.7692 11.3751 13.125V7.875C11.3751 7.23072 11.8975 6.70841 12.5418 6.70833C13.186 6.70833 13.7085 7.23066 13.7085 7.875V13.125C13.7085 13.7693 13.186 14.2917 12.5418 14.2917ZM12.5418 18.3773C11.8975 18.3772 11.3751 17.8549 11.3751 17.2107V17.1992C11.3751 16.5549 11.8975 16.0327 12.5418 16.0326C13.186 16.0326 13.7085 16.5549 13.7085 17.1992V17.2107C13.7085 17.8549 13.186 18.3773 12.5418 18.3773Z"
                        fill="#FF6C6C"
                      />
                    </g>
                    <defs>
                      <filter
                        id="filter0_d_3457_147464"
                        x="0"
                        y="0"
                        width="25.884"
                        height="23.95"
                        filterUnits="userSpaceOnUse"
                        color-interpolation-filters="sRGB"
                      >
                        <feFlood floodOpacity="0" result="BackgroundImageFix" />
                        <feColorMatrix
                          in="SourceAlpha"
                          type="matrix"
                          values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
                          result="hardAlpha"
                        />
                        <feOffset dx="0.8" dy="1.2" />
                        <feComposite in2="hardAlpha" operator="out" />
                        <feColorMatrix
                          type="matrix"
                          values="0 0 0 0 0.537255 0 0 0 0 0.0235294 0 0 0 0 0.0235294 0 0 0 1 0"
                        />
                        <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_3457_147464" />
                        <feBlend
                          mode="normal"
                          in="SourceGraphic"
                          in2="effect1_dropShadow_3457_147464"
                          result="shape"
                        />
                      </filter>
                    </defs>
                  </svg>
                </span>
                <span className="underline">{t("gameAnswer.report")}</span>
              </button>

              <button
                type="button"
                className="hidden lg:flex bg-primary-orange border border-black-300 rounded-md px-10 2xl:px-12 2xl:h-[3.229vw] xl:h-8 md:h-8 h-6 landscape:max-lg:h-7.5 w-fit flex items-center justify-center absolute 2xl:-top-4 -top-1 end-10 z-10"
              >
                <div className="font-bold text-2xl 2xl:text-4xl absolute mt-1 text-white">{t("game1000Question.1000")}</div>
              </button>


              <div className="flex lg:hidden justify-center gap-2 shrink-0 flex-wrap ">
                <div className="skew-x-[-10deg] 2xl:border-[0.104vw] border-[1.5px] border-black-300 bg-gradient-to-r from-yellow-400 via-yellow-500 to-orange-500 flex items-center 2xl:py-[0.5vw] xl:py-1.5 py-1 2xl:px-[1.042vw] lg:px-4 md:px-3 px-2.5 2xl:rounded-[0.26vw] rounded-[5px] 2xl:shadow-[0px_0.156vw_0px_#110706] shadow-[0px_2px_0px_#110706]">
                  <span className="skew-x-[10deg] 2xl:mt-[0.208vw] mt-1 2xl:text-[0.9375vw]/[1.04vw] xl:text-base/tight text-sm/tight font-bold text-white font-garamond">
                    {t("game1000Question.1000PointQuestion")}
                  </span>
                </div>
                {teamNameFromState && (
                  <div className="skew-x-[-10deg] 2xl:border-[0.104vw] border-[1.5px] border-black-300 bg-[#E7E6E6] flex items-center 2xl:py-[0.5vw] xl:py-1.5 py-1 2xl:px-[1.042vw] lg:px-4 md:px-3 px-2.5 2xl:rounded-[0.26vw] rounded-[5px] 2xl:shadow-[0px_0.156vw_0px_#110706] shadow-[0px_2px_0px_#110706]">
                    <span className="skew-x-[10deg] 2xl:mt-[0.208vw] mt-1 2xl:text-[0.9375vw]/[1.04vw] xl:text-base/tight text-sm/tight font-bold text-black-300">{teamNameFromState}</span>
                  </div>
                )}
              </div>

              <div className="flex lg:hidden z-10 justify-center items-center py-3 gap-3 ">
                <GameAnswerTimerClock
                  isSecondChanceActive={isSecondChanceActive}
                  isSecondChanceExpired={isSecondChanceExpired}
                  playerTimerPaused={playerTimerPaused}
                  secondChanceKey={secondChanceKey}
                  questionType={questionData?.question_type}
                  qrTimerCap={qrTimerCap}
                  isMainTimerStopped={isMainTimerStopped}
                  onStartSecondChance={handleStartSecondChance}
                  onSecondChanceTimeUp={handleSecondChanceTimeUp}
                  onResumePlayerTimer={handleResumePlayerTimer}
                  secondChanceSeconds={SECOND_CHANCE_SECONDS}
                  elapsedSecondsRef={elapsedSecondsRef}
                  secondChanceSecondsRef={secondChanceSecondsRef}
                />

                <button
                  type="button"
                  className="bg-[#d76a4d] border border-black-300 rounded-md px-10 2xl:px-12 2xl:h-[3.229vw] xl:h-8 md:h-8 h-6 landscape:max-lg:h-7.5 w-fit flex items-center justify-center z-10"
                >
                  <div className="font-bold text-2xl 2xl:text-4xl absolute mt-1 text-white">{t("game1000Question.1000")}</div>
                </button>

              </div>


              {!showCorrectAnswer && (
                <button
                  type="button"
                  onClick={() => setShowCorrectAnswer(true)}
                  className="skew-x-[-10deg] 2xl:border-[0.104vw] border-[1.5px] border-black-300 bg-primary-orange flex items-center 2xl:py-[0.5vw] xl:py-1.5 py-1 landscape:max-lg:py-0.5 2xl:px-[1.042vw] lg:px-4 md:px-3 px-2.5 2xl:rounded-[0.26vw] rounded-[5px] 2xl:text-[1.5vw]/[1.5vw] xl:text-base/tight text-sm/tight font-bold text-white cursor-pointer justify-center text-nowrap 2xl:shadow-[0px_0.156vw_0px_#110706] shadow-[0px_2px_0px_#110706] absolute -bottom-0.5 end-5 z-10"
                >
                  <span className="skew-x-[10deg] 2xl:mt-[0.208vw] mt-1">{t("game1000Question.viewCorrectAnswer")}</span>
                </button>
              )}



              <div className="flex-1 min-h-0 flex flex-col">
                {!showCorrectAnswer && (
                  <GameAnswerQuestionContent
                    questionData={questionData}
                    currentLanguage={currentLanguage}
                    isSecondChanceActive={isSecondChanceActive}
                    getPreviewTimerSeconds={getPreviewTimerSeconds}
                    SECOND_CHANCE_SECONDS={SECOND_CHANCE_SECONDS}
                    setOptionImagePreviewUrl={setOptionImagePreviewUrl}
                    getLocalizedField={getLocalizedField}
                    t={t}
                  />
                )}
                {showCorrectAnswer && (
                  <GameAnswerCorrectAnswerContent
                    setShowCorrectAnswer={setShowCorrectAnswer}
                    setIsSecondChanceActive={setIsSecondChanceActive}
                    questionData={questionData}
                    currentLanguage={currentLanguage}
                    setOptionImagePreviewUrl={setOptionImagePreviewUrl}
                    getLocalizedField={getLocalizedField}
                    t={t}
                    isSkipLifelineAnswerReveal={false}
                    gameData={gameData}
                    handleAnswerClick={handleAnswerClick}
                    handleNoneAnswered={handleNoneAnswered}
                    onNavigateNextQuestion={() => navigate(`/game/${gameId}`)}
                    whoMadeTheCorrectAnswerText={t("game1000Question.whoMadeTheCorrectAnswer")}
                    noneAnsweredText={t("game1000Question.none")}
                  />
                )}
              </div>
            </div>
          </div>
        </section>

        <section className="game-bottom-banner h-auto lg:h-full flex items-center relative justify-center">
          <div className="w-full 2xl:max-w-[65vw] max-w-335 mx-auto relative z-10 flex flex-col 2xl:gap-[1.25vw] xl:gap-1 md:gap-3 gap-2.5">
            {teamData?.map((team: any) => (
              <div key={team._id}>
                <GameLifeline
                  team={team}
                  score={team.total_points}
                  lifelines={team.lifelines}
                  isCurrentTurn={team._id === teamIdFromState}
                  disableAllLifelines={true}
                />
              </div>
            ))}
          </div>
        </section>

        {/* Answer Modal */}
        {showAnswerModal && (
          <>
            <div className="fixed inset-0 bg-black/80 flex items-center justify-center lg:p-[1%] p-4 z-50">
              <div className="modal bg-white-500 2xl:rounded-[2.083vw] lg:rounded-[40px] md:rounded-4xl rounded-3xl 2xl:p-[2vw] xl:p-10 md:p-7 p-4 relative animate-[slideUp_0.3s_ease-out] lg:border-[0.2vw] border-3 border-primary overflow-auto lg:max-h-[calc(100dvh-11%)] max-h-[calc(100dvh-40px)] scroll-width-small">
                <div className="flex lg:gap-[1vw] gap-2 items-start justify-between w-full 2xl:mb-[1vw] md:mb-4 mb-3">
                  <h1 className="2xl:text-[1.563vw]/[1.7vw] xl:text-3xl/6 text-2xl/5 font-semibold text-primary-orange">
                    {t("game1000Question.selectParticipant")}
                  </h1>
                  <button
                    onClick={() => {
                      setShowAnswerModal(false);
                      setSelectedParticipantIds([]);
                    }}
                    className="lg:w-[1.2vw] lg:h-[1.2vw] md:w-5 w-4 md:h-5 h-4 flex items-center justify-center cursor-pointer ms-auto hover:scale-110 transition-transform duration-300"
                  >
                    <svg className="w-full h-full" width="24" height="24" viewBox="0 0 24 24" fill="none">
                      <path
                        d="M15.9474 12.1123C15.8856 12.0504 15.8856 11.9496 15.9474 11.8878L23.5029 4.3286C24.149 3.68231 24.149 2.63063 23.5029 1.98429L22.0051 0.48573C21.692 0.172468 21.2758 0 20.833 0C20.3902 0 19.9739 0.172468 19.6609 0.485683L12.1054 8.04493C12.0672 8.08316 12.0235 8.09122 11.9937 8.09122C11.9639 8.09122 11.9203 8.08321 11.8821 8.04493L4.32658 0.485683C4.01351 0.172468 3.59726 0 3.15445 0C2.71164 0 2.29544 0.172468 1.98236 0.485683L0.484547 1.98429C-0.161516 2.63063 -0.161516 3.68231 0.484547 4.3286L8.04001 11.8878C8.10185 11.9496 8.10185 12.0504 8.04001 12.1123L0.484547 19.6714C-0.161469 20.3178 -0.161469 21.3694 0.484547 22.0157L1.98236 23.5143C2.29544 23.8275 2.71168 24 3.15445 24C3.59721 24 4.01351 23.8275 4.32658 23.5143L11.882 15.9551C11.9203 15.9169 11.9638 15.9089 11.9937 15.9089C12.0235 15.9089 12.0671 15.9169 12.1053 15.9552L19.6608 23.5143C19.9739 23.8276 20.3901 24 20.8329 24C21.2758 24 21.692 23.8276 22.005 23.5143L23.5028 22.0158C24.1489 21.3695 24.1489 20.3178 23.5028 19.6715L15.9474 12.1123Z"
                        fill="#333938"
                      />
                    </svg>
                  </button>
                </div>

                <div className="w-full 2xl:mb-[1.042vw] mb-5">
                  <svg className="w-full" width="540" height="1" viewBox="0 0 540 1" fill="none">
                    <line
                      y1="-0.5"
                      x2="540"
                      y2="-0.5"
                      transform="matrix(-1 0 0 1 540 1)"
                      stroke="url(#paint0_linear_1000)"
                    />
                    <defs>
                      <linearGradient
                        id="paint0_linear_1000"
                        x1="0"
                        y1="0.5"
                        x2="540"
                        y2="0.5"
                        gradientUnits="userSpaceOnUse"
                      >
                        <stop stopColor="#0B3369" stopOpacity="0" />
                        <stop offset="0.5" stopColor="#7364FF" />
                        <stop offset="1" stopColor="#0B3369" stopOpacity="0" />
                      </linearGradient>
                    </defs>
                  </svg>
                </div>

                {/* Participant List */}
                <div className="grid grid-cols-2 gap-2 2xl:gap-[0.833vw] md:gap-4 border-2 border-black-300 bg-primary-ivory 2xl:p-[1vw] p-4 2xl:rounded-[0.833vw] rounded-xl">
                  {participants?.map((participant) => {
                    const participantId = participant?.user_id?._id;
                    const isSelected = selectedParticipantIds.includes(participantId);
                    const participantPortrait =
                      resolveCharacterUrlFromList(participant?.user_id?.profile_image, portraitCharacters) ||
                      getDefaultPortraitByGender(
                        participant?.user_id?.gender || participant?.gender,
                        portraitCharacters
                      ) ||
                      null;
                    return (
                      <button
                        key={participant._id}
                        onClick={() => handleParticipantClick(participantId)}
                        className={`cursor-pointer w-full min-w-0 flex items-center 2xl:gap-[0.6vw] gap-2`}
                      >
                        {/* <div
                          className={`2xl:w-[1.2vw] 2xl:h-[1.2vw] w-5 h-5 rounded-md flex items-center justify-center transition-all ${isSelected ? "bg-primary" : "bg-white border-2 border-gray-300"
                            }`}
                        >
                          {isSelected && (
                            <svg className="w-3 h-3 text-white" viewBox="0 0 20 20" fill="currentColor">
                              <path
                                fillRule="evenodd"
                                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                clipRule="evenodd"
                              />
                            </svg>
                          )}
                        </div> */}
                        <div
                          className="2xl:w-[2.5vw] xl:w-[40px] lg:w-10 w-10 2xl:min-w-[2.5vw] xl:min-w-[40px] lg:min-w-10 min-w-10 2xl:h-[2.5vw] xl:h-[40px] lg:h-10 h-10 aspect-square overflow-hidden
                              bg-primary-light-orange skew-x-[-10deg] 2xl:border-[0.104vw] border-[1.5px] 2xl:rounded-[0.26vw] rounded-[5px] 2xl:shadow-[0px_0.156vw_0px_#110706] shadow-[0px_2px_0px_#110706] flex justify-center items-center"
                        >
                          {participantPortrait ? (
                            <img
                              src={participantPortrait}
                              alt={participant?.user_id?.full_name}
                              className="w-full h-full object-cover"
                            />
                          ) : <span className="text-center text-xl">
                            {participant?.user_id?.full_name?.charAt(0)?.toUpperCase() || "?"}
                          </span>}
                        </div>
                        <div
                          className={`min-w-0 flex-1 skew-x-[-10deg] 2xl:border-[0.104vw] border-[1.5px] border-black-300 flex items-center 2xl:py-[0.54vw] py-1.5 2xl:px-[1.042vw] lg:px-4 md:px-3  px-2.5 2xl:rounded-[0.26vw] rounded-[5px] 2xl:text-[0.9375vw]/[1.04vw] text-sm/tight md:text-base/tight font-bold cursor-pointer justify-center 2xl:shadow-[0px_0.156vw_0px_#110706] shadow-[0px_2px_0px_#110706]
                        ${isSelected ? "bg-primary-orange text-white" : "bg-[#F2BF7D] text-black-300"}`}
                        >
                          <span className={`skew-x-[10deg] 2xl:mt-[0.208vw] mt-1 text-start w-full break-words`}>
                            {participant?.user_id?.full_name}
                          </span>
                        </div>
                      </button>
                    );
                  })}
                </div>

                {/* {selectedParticipantIds.length > 0 && (
                  <p className="2xl:text-[0.75vw] text-sm text-primary-orange font-bold 2xl:mt-[0.8vw] mt-3 text-center">
                    {selectedParticipantIds.length} {t("game1000Question.participant")}
                    {selectedParticipantIds.length > 1 ? t("game1000Question.s") : ""} {t("game1000Question.selected")}
                  </p>
                )} */}
                <PrimaryBtn
                  onClick={handleConfirmAnswer}
                  className="!mb-0 w-full min-w-full 2xl:mt-[1vw] mt-4"
                >{`${t("game1000Question.confirmAnswer")}`}</PrimaryBtn>
              </div>
            </div>
            <style>{`
              @keyframes slideUp {
                from { opacity: 0; transform: translateY(20px); }
                to { opacity: 1; transform: translateY(0); }
              }
            `}</style>
          </>
        )}

        {optionImagePreviewUrl && (
          <div
            className="fixed inset-0 bg-black/80 flex items-center justify-center z-[60] p-4"
            onClick={() => setOptionImagePreviewUrl(null)}
            role="presentation"
          >
            <div
              className="relative max-w-[min(90vw,720px)] max-h-[min(85dvh,720px)] p-2 bg-white-500 rounded-xl border-[1.5px] border-black-300 shadow-lg"
              onClick={(e) => e.stopPropagation()}
              role="dialog"
              aria-modal="true"
            >
              <button
                type="button"
                onClick={() => setOptionImagePreviewUrl(null)}
                className="absolute -top-2 -end-2 z-10 w-8 h-8 flex items-center justify-center rounded-full bg-primary-yellow border-[1.5px] border-black-300 shadow-[0px_2px_0px_#110706] hover:scale-105 transition-transform"
                aria-label="Close"
              >
                <span className="text-black-300 font-bold leading-none text-lg">×</span>
              </button>
              <img
                src={optionImagePreviewUrl}
                alt=""
                className="max-w-full max-h-[min(80dvh,680px)] w-auto h-auto object-contain mx-auto block rounded-md"
              />
            </div>
          </div>
        )}

        {showReportModal && (
          <ReportQuestionModal
            isOpen={showReportModal}
            onClose={() => setShowReportModal(false)}
            questionData={questionData}
            quistionIdToReport={questionData?.question_id}
          />
        )}
      </div>
    </>
  );
};

export default Game1000Question;
